import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreatePersonalTarget, useCreateTarget } from '@/features/memorization/hooks';
import { useSurahs } from '@/features/quran/hooks';
import { ApiError } from '@/lib/api/client';

/**
 * `POST /memorization/targets` (`createTargetSchema`). Surah is picked from
 * the real Quran structural reference (`GET /quran/surahs`) rather than a
 * bare number field. The backend enforces no overlap check between targets
 * and no minimum/maximum date - neither is fabricated here.
 *
 * `mode="personal"` posts to `POST /memorization/personal-targets`
 * instead (ADR-031, Decision 6): a solo learner setting a target for
 * themselves inside their own personal space. The form is identical - the
 * fields, the validation, and the resulting row are the same; only the
 * endpoint and its authorization precondition differ - so this is one
 * dialog with two callers rather than a near-duplicate second dialog.
 * `studentId` is still required in both modes: in personal mode it is not
 * sent to the server (which derives it from the caller), and is used only
 * to invalidate the right cached queries.
 */
export function CreateTargetDialog({
  studentId,
  open,
  onOpenChange,
  mode = 'assigned',
}: {
  readonly studentId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly mode?: 'assigned' | 'personal';
}): JSX.Element {
  const [surah, setSurah] = useState('');
  const [startAyah, setStartAyah] = useState('');
  const [endAyah, setEndAyah] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const { data: surahs, isLoading: surahsLoading } = useSurahs();
  const isPersonal = mode === 'personal';
  // Both hooks are called unconditionally (rules of hooks); only the one
  // matching `mode` is ever mutated.
  const createAssigned = useCreateTarget(studentId);
  const createPersonal = useCreatePersonalTarget(studentId);
  const create = isPersonal ? createPersonal : createAssigned;

  const selectedSurah = surahs?.find((item) => String(item.number) === surah);

  function reset(): void {
    setSurah('');
    setStartAyah('');
    setEndAyah('');
    setTargetDate('');
    setValidationError(null);
    create.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    const surahNumber = Number(surah);
    const startAyahNumber = Number(startAyah);
    const endAyahNumber = Number(endAyah);

    if (surah === '' || !Number.isInteger(surahNumber)) {
      setValidationError('Pilih surah.');
      return;
    }
    if (startAyah === '' || !Number.isInteger(startAyahNumber) || startAyahNumber < 1) {
      setValidationError('Masukkan ayat awal yang valid.');
      return;
    }
    if (endAyah === '' || !Number.isInteger(endAyahNumber) || endAyahNumber < 1) {
      setValidationError('Masukkan ayat akhir yang valid.');
      return;
    }
    if (endAyahNumber < startAyahNumber) {
      setValidationError('Ayat akhir tidak boleh lebih kecil dari ayat awal.');
      return;
    }

    const range = {
      surah: surahNumber,
      startAyah: startAyahNumber,
      endAyah: endAyahNumber,
      targetDate: targetDate.length > 0 ? targetDate : null,
    };
    const onSuccess = (): void => {
      handleOpenChange(false);
    };

    if (isPersonal) {
      createPersonal.mutate(range, { onSuccess });
      return;
    }
    createAssigned.mutate({ studentId, ...range }, { onSuccess });
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Target Hafalan</DialogTitle>
          <DialogDescription>
            {isPersonal
              ? 'Tentukan sendiri surah dan rentang ayat yang ingin Anda hafal.'
              : 'Tentukan surah dan rentang ayat untuk target baru.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="target-surah">Surah</Label>
            <Select value={surah} onValueChange={setSurah}>
              <SelectTrigger id="target-surah">
                <SelectValue placeholder={surahsLoading ? 'Memuat...' : 'Pilih surah'} />
              </SelectTrigger>
              <SelectContent>
                {surahs?.map((item) => (
                  <SelectItem key={item.number} value={String(item.number)}>
                    {item.number}. {item.nameLatin}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSurah !== undefined && (
              <p className="text-xs text-muted-foreground">
                {selectedSurah.nameLatin} memiliki {selectedSurah.ayahCount} ayat.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="target-start-ayah">Ayat Awal</Label>
              <Input
                id="target-start-ayah"
                type="number"
                min={1}
                value={startAyah}
                onChange={(event) => {
                  setStartAyah(event.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="target-end-ayah">Ayat Akhir</Label>
              <Input
                id="target-end-ayah"
                type="number"
                min={1}
                value={endAyah}
                onChange={(event) => {
                  setEndAyah(event.target.value);
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="target-date">Tanggal Target (opsional)</Label>
            <Input
              id="target-date"
              type="date"
              value={targetDate}
              onChange={(event) => {
                setTargetDate(event.target.value);
              }}
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
